import { CountdownTimer, LayoutType } from "@kalyzee/escaet-ui";
import {
  isMeetingSessionRoleGreaterThanOrEqual,
  isSpectator,
  MeetingSessionOvenMediaEmitterManagerBaseData,
  MeetingSessionOvenMediaReceiverManagerBaseData,
  MeetingSourceElement,
  MeetingSourceElementRef,
} from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { OvenMediaEmitterWebRTCSession, OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingRoomStatus, MeetingRoomType, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useRef, useState } from "react";
import { getDelayBeforeAutoJoinActivity } from "../../../constants/meeting";
import { getAudioContextForVolumeAnalyzer } from "../../../helpers/audio";
import { getFormattedUserFromMeetingSession } from "../../../helpers/meeting";
import { toastError } from "../../../helpers/toast";
import { expertWebcamName, isWebcamName, pedagogyWebcamName } from "../../../helpers/webcam";
import { useMeetingSelector, useMeetingWebcamFeedbackSource } from "../../../hooks/meeting";
import { useActivityData, usePopulatedMeetingData } from "../../../hooks/populatedData";
import { PopulatedMeetingSession } from "../../../interfaces/populatedData";
import { useMediaContext } from "../../../pages/master/common/private/mediaContext";
import { ExpertContextState } from "../../../pages/master/expert/context";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import { ScreenMediaName } from "../../media/ScreenShare";
import { PlayerItem } from "../interfaces";
import MeetingSessionMediaPlayer from "../MeetingSessionMediaPlayer";
import MeetingSessionMediaStreamPlayer from "../MeetingSessionMediaStreamPlayer";
import { MeetingPlayerHighlightContainer } from "./MeetingPlayerHighlightContainer";
import { MeetingPlayerTriLayoutContainer } from "./MeetingPlayerTriLayoutContainer";
import { FullScreenRenderer } from "../../renderers/FullScreenRenderer";
import { FullScreenButton } from "../../utils/FullScreenButton";

export interface ExpertsToRemotesContainerProps {
  meetingContainerAPI: MeetingContainerAPI | undefined;
  context: ExpertContextState | PedagogyContextState | undefined;
  expertSessions?: PopulatedMeetingSession[];
  remoteSessions?: PopulatedMeetingSession[];
  layout?: LayoutType;
  showRemoteGroup?: boolean;
  webcamAndScreenOnly?: boolean;
  showSelf?: "new_source" | "current_video";
  style?: React.CSSProperties;
  onClickSession?: (session: PopulatedMeetingSession) => void;
  renderItem?: (item: PlayerItem, children: React.ReactNode) => React.ReactNode;
  renderItemExtraButtons?: (item: PlayerItem) => React.ReactNode;
}

export const ExpertsToRemotesContainer = ({
  meetingContainerAPI,
  context,
  expertSessions = [],
  remoteSessions = [],
  layout = LayoutType.Square,
  showRemoteGroup,
  webcamAndScreenOnly,
  showSelf,
  style,
  onClickSession,
  renderItem,
  renderItemExtraButtons,
}: ExpertsToRemotesContainerProps) => {
  // ---------------------------------------------------------------------- //
  // --------------------------- STATES ----------------------------- //
  // ---------------------------------------------------------------------- //

  const [unconfirmedSessions, setUnconfirmedSessions] = useState<{ session: PopulatedMeetingSession; estimatedArrival: number }[]>([]);

  const webcamFeedbackSourceElementRef = useRef<MeetingSourceElementRef>();

  const mediaCtx = useMediaContext();

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [session] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const [emissionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.emissionSessions);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const activityData = useActivityData(populatedMeetingData);

  const webcamFeedbackSource = useMeetingWebcamFeedbackSource();

  const updateWebcamFeedbackSource = useCallback(() => {
    if (!context) return;
    const ref = webcamFeedbackSourceElementRef.current;
    if (!ref) return;
    const source = ref.getData()?.source;
    if (!source) return;
    ref.enableAudio(mediaCtx.audioStreamEnabled);
    if (source.extra.type === "camera") ref.enableVideo(mediaCtx.videoStreamEnabled);
    if (source.extra.type === "screen") ref.enableVideo(mediaCtx.screensVideoStreamEnabled);
    if (source.extra.type === "board") ref.enableVideo(mediaCtx.boardsVideoStreamEnabled);
  }, [context]);

  useEffect(() => {
    updateWebcamFeedbackSource();
  }, [updateWebcamFeedbackSource]);

  useEffect(() => {
    const unconfirmed: PopulatedMeetingSession[] = [];
    activityData?.sessions?.forEach((s) => {
      if (activityData?.confirmedSessions?.some((c) => c.id === s.id)) return;
      // session isn't confirmed
      unconfirmed.push(s);
    });
    const currEstimatedArrival = new Date().getTime() + getDelayBeforeAutoJoinActivity(activityData?.mode);
    setUnconfirmedSessions((previous) =>
      unconfirmed.map((s) => previous.find((p) => p.session.id === s.id) || { session: s, estimatedArrival: currEstimatedArrival })
    );
  }, [activityData]);

  // ---------------------------------------------------------------------- //
  // --------------------------- HANDLERS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const bindWebcamMedia = (mediaStream: MediaStream) => {
    context?.setWebcam({ mediaStream });
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting || !session) return null;

  const items: PlayerItem[] = [];
  remoteSessions.forEach((session) => {
    session.videoSessions.forEach((videoSession) => {
      const currentRoom = session?.user?.group?.room;
      if (webcamAndScreenOnly) {
        const mediaName = videoSession.data.mediaSession.name;
        if (!mediaName || !(isWebcamName(mediaName) || mediaName === ScreenMediaName)) return;
      }
      items.push(
        new PlayerItem({
          type: "video_session",
          videoSession,
          extra: {
            meetingSession: session, // we store this here so we don't lose this connection
            isLeaderSession:
              session &&
              currentRoom?.type === MeetingRoomType.Group &&
              currentRoom?.status === MeetingRoomStatus.Ongoing &&
              currentRoom?.leaderUserId === session.user?.id,
          },
        })
      );
    });
  });

  const renderLoadingUsers = () => {
    let names: string[] = [];
    let latestArrival = 0;

    unconfirmedSessions.forEach((session) => {
      const formatted = getFormattedUserFromMeetingSession(session.session.session, meeting);
      if (formatted.fullname) {
        if (names.length < 3) names.push(formatted.fullname);
        if (session.estimatedArrival > latestArrival) {
          latestArrival = session.estimatedArrival;
        }
      }
    });

    const remaining = unconfirmedSessions.length - 3;
    if (remaining > 0) {
      names.push(`${remaining} autre${remaining > 1 ? "s" : ""}`);
    }

    let namesStr = "";
    names.forEach((name, idx) => {
      if (idx > 0) {
        if (idx === names.length - 1) {
          namesStr += " et ";
        } else {
          namesStr += ", ";
        }
      }
      namesStr += name;
    });

    let action = "monter au tableau.";
    if (activityData?.mode !== "highlight") action = "rejoindre la discution.";

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "24px",
          fontWeight: 600,
          color: "rgb(102 102 102)",
          gap: "10px",
          textAlign: "center",
          padding: "0px 15%",
          whiteSpace: "pre",
        }}
      >
        {names.length ? (
          <>
            {`${namesStr} ${names.length > 1 ? "\nse préparent" : "se prépare"} à ${action}`}
            <CountdownTimer targetDate={new Date(latestArrival)} style={{ color: "rgb(102 102 102)" }} />
          </>
        ) : (
          "En attente de participants ..."
        )}
      </div>
    );
  };

  const renderRemotePlayers = () => {
    return (
      <div
        style={{
          height: "100%",
          flex: 2,
          minWidth: 0,
          gap: "1em",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {items.length ? (
          <MeetingPlayerTriLayoutContainer
            meeting={meeting}
            items={items}
            renderItem={(rendererProvider, item, isSelectedPlayer) => {
              const itemData = item?.getData();
              if (itemData?.type !== "video_session") return <div />;
              const names = getFormattedUserFromMeetingSession(itemData.videoSession.data.session, meeting);
              const session = itemData.extra?.meetingSession;
              const isLeader = itemData.extra?.isLeaderSession;
              const isPeda = session?.session?.extra?.isPedagogy;
              const isExpert = !isPeda && session && isMeetingSessionRoleGreaterThanOrEqual(session?.session.role, MeetingSessionRole.MODERATOR);
              return rendererProvider((children, context) => (
                <FullScreenRenderer
                  render={(enabled, setEnabled) => (
                    <MeetingPlayerHighlightContainer
                      style={{ width: "100%", height: "100%" }}
                      fullName={names.fullname}
                      groupName={showRemoteGroup ? itemData.extra?.meetingSession?.user.group?.room.name : undefined}
                      isPlayingSound={context?.isPlayingSound}
                      isScreenShare={itemData.videoSession.data.mediaSession.name === ScreenMediaName}
                      isMainMode={isSelectedPlayer}
                      soundEffect
                      onClick={() => {
                        if (enabled) setEnabled(false);
                        else session && onClickSession?.(session);
                      }}
                      extraButtons={[renderItemExtraButtons?.(item), <FullScreenButton enabled={enabled} setEnabled={setEnabled} transparentBg />]}
                      isLeader={isLeader}
                      isExpert={isExpert}
                      isPeda={isPeda}
                    >
                      {renderItem ? renderItem(item, children) : children}
                    </MeetingPlayerHighlightContainer>
                  )}
                />
              ));
            }}
            layout={layout}
            style={{ flex: 1, minHeight: 0 }}
          />
        ) : null}
        {(unconfirmedSessions.length || !items.length) && renderLoadingUsers()}
      </div>
    );
  };

  const renderExpertPlayers = () => {
    const videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[] = [];
    expertSessions.forEach((e) => {
      e.videoSessions?.forEach((v) => {
        if (v.data.mediaSession.name === pedagogyWebcamName || v.data.mediaSession.name === expertWebcamName) {
          videoSessions.push(v);
        }
      });
    });
    const selfIsSpectator = isSpectator(session);
    const self = showSelf && !selfIsSpectator;
    const selfIsPeda = session.extra?.isPedagogy;

    if (!videoSessions.length && !self) return null;

    const renderSelf = () => {
      if (showSelf === "new_source") {
        return (
          <MeetingPlayerHighlightContainer
            key={`meeting-expert-webcam-source`}
            style={{ minHeight: 0, flex: "0 1 auto", background: "transparent" }}
            isExpert={!selfIsPeda}
            isPeda={selfIsPeda}
            fullName={`${getFormattedUserFromMeetingSession(session, meeting).fullname} (Vous)`}
          >
            <MeetingSourceElement
              style={{ width: "100%", height: "100%" }}
              ref={webcamFeedbackSourceElementRef}
              source={webcamFeedbackSource}
              audioContext={getAudioContextForVolumeAnalyzer()}
              callbacks={{
                track: (_, data) => {
                  const mediaStream = data.media;
                  if (!mediaStream) return;
                  const audio = mediaStream.getAudioTracks();
                  const video = mediaStream.getVideoTracks();
                  if (audio && video) {
                    updateWebcamFeedbackSource();
                    bindWebcamMedia(mediaStream);
                  }
                },
                error: (type) => {
                  if (type === "audio") {
                    toastError("Veuillez autoriser le navigateur à accéder au microphone");
                  }
                  if (type === "video") {
                    toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                  }
                },
              }}
            />
          </MeetingPlayerHighlightContainer>
        );
      }
      const currentVideoSessions: OvenMediaEmitterWebRTCSession<MeetingSessionOvenMediaEmitterManagerBaseData<any>>[] = [];
      emissionSessions?.forEach((s) => {
        if (s.data.mediaSession.name === pedagogyWebcamName || s.data.mediaSession.name === expertWebcamName) {
          if (s.stream) currentVideoSessions.push(s);
        }
      });
      const isPeda = session.extra?.isPedagogy;
      const isExpert = !isPeda;
      return currentVideoSessions.map((s) => {
        const stream = s.stream?.getMediaStream();
        return (
          <MeetingSessionMediaStreamPlayer
            key={`meeting-expert-webcam-${s.getId()}`}
            style={{ width: "100%", height: "100%" }}
            meeting={meeting}
            sessionId={session.id}
            mediaSessionId={s.data.mediaSession?.id!}
            mediaStream={stream}
            muted
            renderCustomContainer={(children, context) => {
              const names = getFormattedUserFromMeetingSession(session, meeting);
              return (
                <MeetingPlayerHighlightContainer
                  fullName={names.fullname}
                  isPlayingSound={context?.isPlayingSound}
                  isExpert={isExpert}
                  isPeda={isPeda}
                  soundEffect
                  style={{ minHeight: 0, flex: "0 1 auto", width: "100%", height: "unset" }}
                >
                  {children}
                </MeetingPlayerHighlightContainer>
              );
            }}
          />
        );
      });
    };

    return (
      <div
        style={{
          flex: 1,
          height: "100%",
          maxHeight: "100%",
          width: "100%",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
          gap: "0.5em",
        }}
      >
        {videoSessions.map((s) => {
          const isPeda = s.data.session.extra?.isPedagogy;
          const isExpert = !isPeda;
          return (
            <MeetingSessionMediaPlayer
              key={`meeting-expert-webcam-${s.getId()}`}
              style={{ width: "100%", height: "100%" }}
              meeting={meeting}
              sessionId={s.data.session.id}
              mediaSessionId={s.data.mediaSession?.id!}
              ovenMediaSession={s}
              muted
              hideSoundEffect
              autoOnOffVideo
              renderCustomContainer={(children, context) => {
                const names = getFormattedUserFromMeetingSession(s.data.session, meeting);
                return (
                  <FullScreenRenderer
                    render={(enabled, setEnabled) => (
                      <MeetingPlayerHighlightContainer
                        fullName={names.fullname}
                        isPlayingSound={context?.isPlayingSound}
                        isExpert={isExpert}
                        isPeda={isPeda}
                        soundEffect
                        style={{ minHeight: 0, flex: "0 1 auto", width: "100%", height: "unset" }}
                        onClick={() => {
                          if (enabled) setEnabled(false);
                        }}
                        extraButtons={[<FullScreenButton enabled={enabled} setEnabled={setEnabled} transparentBg />]}
                      >
                        {children}
                      </MeetingPlayerHighlightContainer>
                    )}
                  />
                );
              }}
            />
          );
        })}
        {showSelf ? renderSelf() : null}
      </div>
    );
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "1em",
        ...(style ?? {}),
      }}
    >
      {renderRemotePlayers()}
      {renderExpertPlayers()}
    </div>
  );
};
